import { apiBaseUrl, apiRequest } from './client';

/**
 * Why a number is missing, not only that it is.
 *
 * `withheld` is her own setting; `unsupported` is the format never having had
 * it — a photo has no view count. Both arrive as `null`, and only one of them
 * is worth a sentence on screen.
 */
export type MetricState = 'returned' | 'withheld' | 'unsupported';

export interface ReadingMetric {
  kind: 'likes' | 'comments' | 'views' | 'engagement';
  value: number | null;
  state: MetricState;
}

export interface ReadingScene {
  label: string;
  headline: string;
  body: string;
}

/** The half that answers as soon as the lookup does — the server's `profile` frame. */
export interface ProfileHalf {
  handle: string;
  /**
   * The public endpoint answers with the most recent posts and a cursor, so
   * this is what was read and not what exists. Every sentence about the span
   * comes from these dates.
   */
  window: {
    postsRead: number;
    oldestAt: string;
    newestAt: string;
    partial: boolean;
  };
  topPost: {
    shortcode: string;
    permalink: string;
    format: 'reel' | 'video' | 'carousel' | 'image';
    caption: string;
    publishedAt: string;
    metrics: ReadingMetric[];
    /** This service's own path. The CDN link never reaches the browser. */
    thumbnailUrl: string | null;
  };
  confidence: { level: 'high' | 'medium' | 'low'; lift: number };
  /** False when the service has nowhere to store a lead, so the page never asks. */
  offerAvailable: boolean;
  provenance: { evidenceClass: string; dataSource: string };
}

/** The half that waits on the model — the server's `script` frame. */
export interface ScriptHalf {
  whyItWorked: string[];
  script: {
    label: string;
    scenes: ReadingScene[];
    remaining: number;
    captionIncluded: boolean;
  };
}

export type ProfileReading = ProfileHalf & ScriptHalf;

/**
 * Her address, in exchange for the reading landing in her inbox.
 *
 * `sent` comes back from the service rather than being assumed here: a message
 * can fail to leave — a bounced address, a revoked key — and a screen that says
 * "mandei" either way is claiming a delivery nobody made.
 */
export function saveReadingLead(email: string, handle: string): Promise<{ sent: boolean }> {
  return apiRequest<{ sent: boolean }>('/v1/profile-reading/lead', {
    method: 'POST',
    anonymous: true,
    body: { email, instagramHandle: handle }
  });
}

/** The reading returns a path; `<img src>` needs the whole address. */
export function thumbnailSrc(path: string): string {
  return `${apiBaseUrl()}${path}`;
}
